// @flow

import * as React from 'react';

import { contactbook } from '@gandi/redux-resource-contactbook';
import { useTranslator } from '@gandi/translate';
import { Base, Spinner } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import {
  selectCurrentContactByRoles,
  selectIsDomainTransferWithDifferentOwner,
  selectConflictingTLDs,
  selectPayloadOwnerProcess,
  selectHasCreateDraft,
  selectHasTransferDraft,
  selectSetupOwner,
  selectIsPayloadInvalid,
} from '../../../modules/selectors';
import type { CreateOrTransfer } from '../../../types';

import RoleContactCard from './RoleContactCard';
import WarningBox from './WarningBox';

type Props = {
  owner?: any,
  process: CreateOrTransfer,
};

export default function Contacts({
  owner,
  process,
}: Props): React.MixedElement {
  const { __ } = useTranslator();

  const hasCreateDraft = useAppSelector(selectHasCreateDraft);
  const hasTransferDraft = useAppSelector(selectHasTransferDraft);

  const isPayloadInvalid = useAppSelector(selectIsPayloadInvalid);

  const setupOwner = useAppSelector(selectSetupOwner);
  const isFetchingContactsWithRole = useAppSelector(state =>
    contactbook.selectContactsWithRoleIsFetching(state, setupOwner),
  );

  const isFetchingOwnerExtraParams = useAppSelector(state =>
    contactbook.selectIsFetchingOwnerContactExtraParams(state, setupOwner),
  );

  const contacts = useAppSelector(state =>
    selectCurrentContactByRoles(state, process),
  );

  const conflictingTLDs = useAppSelector(selectConflictingTLDs);

  // createAlertBox
  const [showWarningCreate, setShowWarningCreate] = React.useState(
    !isPayloadInvalid && conflictingTLDs.length > 0 && !hasCreateDraft,
  );

  // transferAlertBox
  const [showWarningTransfer, setShowWarningTransfer] = React.useState(
    !isPayloadInvalid && !hasTransferDraft,
  );

  const isDomainTransferWithDifferentOwner = useAppSelector(state =>
    owner ? selectIsDomainTransferWithDifferentOwner(state, owner) : false,
  );

  React.useEffect(() => {
    setShowWarningTransfer(
      isDomainTransferWithDifferentOwner && !isPayloadInvalid,
    );
  }, [isDomainTransferWithDifferentOwner, isPayloadInvalid]);

  const readOnly =
    process === 'create' ? showWarningCreate : showWarningTransfer;

  const loading =
    !owner || isFetchingContactsWithRole || isFetchingOwnerExtraParams;

  const ownerPayload = useAppSelector(state =>
    selectPayloadOwnerProcess(state, process),
  );

  return (
    <>
      <Base tagName="h1" marginBottom="small">
        {__('Domain Contacts')}
      </Base>
      {loading && <Spinner centered />}
      {!loading &&
        ((showWarningCreate && process === 'create') ||
          (showWarningTransfer && process === 'transfer')) && (
          <WarningBox
            createTlds={conflictingTLDs}
            process={process}
            onClickEdit={
              process === 'create'
                ? () => setShowWarningCreate(false)
                : () => setShowWarningTransfer(false)
            }
          />
        )}
      {!loading && contacts && (
        <Base flex flexWrap flexJustifyBetween>
          {['owner', 'admin', 'tech', 'bill'].map(role => (
            <RoleContactCard
              role={role}
              contact={
                role === 'owner' ? ownerPayload || owner : contacts[role]
              }
              process={process}
              owner={owner}
              readOnly={readOnly}
              key={`${role}-role-contact-card`}
            />
          ))}
        </Base>
      )}
    </>
  );
}
